import {Injectable} from '@angular/core';
import {CalendarConnectionStatus} from '@app/domain/one_to_one/model/calendar-connected.model';
import {CalendarAPIService} from '@app/shared/api/calendar.api.service';
import {UserAPIService} from '@app/shared/api/user.api.service';
import {Globals} from '@app/shared/globals/globals';
import {NotifyUtils} from '@app/shared/utils/notify.utils';
import {Observable, firstValueFrom} from 'rxjs';
import {ConnectionSettingsAPIService} from '../../api/connection-settings/connection-settings-api.service';
import {SettingsMessages} from '../../locale/settings.messages';
import {ConnectionSettingsDto} from '../../model/connection-settings.model';
import {AuthAPIService} from '@app/shared/auth/auth.api.service';
import {CompanyFeatures} from "@models/company-features.model";
import { InFlightRegistry } from '@app/shared/utils/in-flight-registry';

const WRITE_KEY = 'write';

@Injectable({
  providedIn: 'root'
})
export class ConnectionSettingsBusinessService {

  // A write already in flight coalesces a duplicate call onto the same promise (see InFlightRegistry)
  // rather than rejecting it, which previously surfaced as an unhandled rejection (Sentry FRONTEND-3HT).
  private writes = new InFlightRegistry();

  constructor(
    private connectionSettingsAPIService: ConnectionSettingsAPIService,
    private userAPIService: UserAPIService,
    private calendarAPIService: CalendarAPIService,
    private authAPIService: AuthAPIService,
    private notifyUtils: NotifyUtils,
    public globals: Globals
  ) { }

  get(): Observable<ConnectionSettingsDto> {
    return this.connectionSettingsAPIService.get();
  }

  update(userConnectionSettings: ConnectionSettingsDto): Promise<ConnectionSettingsDto> {
    return this.writes.run(WRITE_KEY, () =>
      firstValueFrom(this.connectionSettingsAPIService.update(userConnectionSettings))
        .then(res => {
          console.log(res);
          this.notifyUtils.notify(SettingsMessages.SUCCESS_PROFILE_UPDATED);
          this.globals.user.configuration.notificationsViaEmailEnabled = res.notificationsViaEmailEnabled;
          this.globals.user.configuration.notificationsViaSlackEnabled = res.notificationsViaSlackEnabled;
          this.globals.user.configuration.notificationsViaTeamsEnabled = res.notificationsViaTeamsEnabled;
          return res;
        })
        .then(res => {
          this.isCalendarConnected().subscribe(resp => {
            if (resp.connected && resp.provider === 'GOOGLE' && this.globals.hasFeature(CompanyFeatures.SSO_GOOGLE)) {
              if (!userConnectionSettings.googleLinked) {
                this.authAPIService.revokeMyGoogleAccess().subscribe(response => {
                  if (response === 1) {
                    this.notifyUtils.notify('Google Account Access Revoked successfully');
                    this.authAPIService.logout();
                  }
                });
              }
            }
          });

          return res;
        })
    );
  }

  isSlackAccountLinked(): Observable<boolean> {
    return this.userAPIService.isSlackAccountLinked();
  }

  isTeamsAccountLinked(): Observable<boolean> {
    return this.userAPIService.isTeamsAccountLinked();
  }

  isCalendarConnected(): Observable<CalendarConnectionStatus> {
    return this.calendarAPIService.isConnected();
  }
}
